import { cn } from '../../utils'
import { Lock, X, Zap } from 'lucide-react'
import { ReactNode, useEffect } from 'react'
import { useEnrichedBilling } from '../../hooks'

export interface UpgradeOverlayProps {
  /**
   * The content to overlay (will be blurred when showing upgrade prompt)
   */
  children: ReactNode

  /**
   * Context identifier for where the overlay is shown (e.g., 'highlights', 'byok')
   * Used for analytics tracking
   */
  context: string

  /**
   * Title of the feature being locked
   */
  featureTitle: string

  /**
   * Description/message about the feature
   */
  message?: string

  /**
   * Icon to show (lock for requires-pro, zap for limits)
   */
  icon?: 'lock' | 'zap'

  /**
   * Optional className for the container
   */
  className?: string

  /**
   * Whether data is still loading (don't show overlay while loading)
   */
  isLoading?: boolean

  /**
   * Whether to show the image header (defaults to true for Highlights context)
   */
  showImage?: boolean

  /**
   * Custom image URL (overrides default image)
   */
  imageUrl?: string
}

/**
 * UpgradeOverlay - A smart wrapper that blurs pro-only content for free users
 * and tells them the feature needs a paid plan. It has no CTA. Plans are
 * picked from the Subscription settings tab.
 *
 * Used for Highlights, BYOK, and other pro-only features.
 *
 * Automatically:
 * - Shows overlay for free users only
 * - Hides for Pro/Trial users and legacy billing users
 * - Tracks analytics events
 */
export const UpgradeOverlay: React.FC<UpgradeOverlayProps> = ({
  children,
  context,
  featureTitle,
  message,
  icon = 'lock',
  className,
  isLoading,
  showImage,
  imageUrl,
}) => {
  const { paywallState } = useEnrichedBilling()

  const isFree = paywallState.plan === 'free'

  // Track when overlay is shown
  useEffect(() => {
    if (isFree && !paywallState.isLegacyBilling && !isLoading) {
      window.ampltd.trackEvent('Upgrade Overlay', {
        action: 'shown',
        context,
        feature: featureTitle,
      })
    }
  }, []) // Only track on mount

  const handleClose = () => {
    window.ampltd.trackEvent('Upgrade Overlay', {
      action: 'dismissed',
      context,
      feature: featureTitle,
    })
  }

  // Don't show overlay for non-free users, legacy billing users, or while loading
  const shouldShow = isFree && !paywallState.isLegacyBilling && !isLoading

  if (!shouldShow) {
    return <>{children}</>
  }

  const Icon = icon === 'lock' ? Lock : Zap
  const defaultMessage = message || `${featureTitle} needs a paid plan. Pick one in Subscription settings.`
  const shouldShowImage = showImage ?? context.toLowerCase().includes('highlights')
  const defaultImageUrl =
    'https://storage.googleapis.com/cdn.highlightai.com/media/product/payment/paywall-highlights.webp'
  const displayImageUrl = imageUrl || defaultImageUrl

  const maskStyle: React.CSSProperties = {
    WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 100%)',
    maskImage: 'linear-gradient(to top, transparent 0%, black 100%)',
  }

  return (
    <div className={cn('relative w-full', className?.includes('h-') ? '' : 'h-full', className)}>
      {/* Blurred content */}
      <div className="pointer-events-none h-full w-full select-none overflow-hidden rounded-2xl">
        <div className="h-full w-full blur-[2px]" style={maskStyle}>
          {children}
        </div>
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center rounded-2xl">
        <div
          className={cn(
            'relative flex w-full flex-col overflow-hidden rounded-[22.4px] bg-[#323231] shadow-xl',
            imageUrl ? 'max-w-[360px]' : 'max-w-md',
          )}
        >
          {/* Image */}
          {shouldShowImage && (
            <img
              src={displayImageUrl}
              alt=""
              className={cn('h-[166px] w-[360px] object-cover', imageUrl ? 'object-left object-top' : 'w-full')}
            />
          )}

          {/* Content */}
          <div className="flex flex-col gap-4 p-4">
            {/* Title */}
            <p className="font-inter text-[18px] font-medium leading-[20px] tracking-[-0.225px] text-white/90">
              {`Unlock ${featureTitle}`}
            </p>

            {/* Message */}
            <p className="font-inter pb-0.5 text-sm font-normal leading-[22px] tracking-[-0.18px] text-white/60">
              {defaultMessage}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
